import { useState } from 'react'
import {
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from '@mui/material'
import { Add, Delete, KeyboardArrowDown, KeyboardArrowUp, ExpandMore } from '@mui/icons-material'
import { NorthStarFieldDef, NorthStarFieldType } from '../../services/api'

const FIELD_TYPES: NorthStarFieldType[] = [
  'text',
  'richText',
  'number',
  'numberRange',
  'list',
  'enum',
  'dimensions',
  'trajectory',
  'repeater',
]

interface Props {
  fields: NorthStarFieldDef[]
  onChange: (next: NorthStarFieldDef[]) => void
  depth?: number
}

export default function TemplateFieldsEditor({ fields, onChange, depth = 0 }: Props) {
  const [expandedKey, setExpandedKey] = useState<string | null>(null)

  const update = (i: number, f: NorthStarFieldDef) => {
    const next = [...fields]
    next[i] = f
    onChange(next)
  }

  const remove = (i: number) => onChange(fields.filter((_, idx) => idx !== i))

  const move = (i: number, delta: number) => {
    const j = i + delta
    if (j < 0 || j >= fields.length) return
    const next = [...fields]
    ;[next[i], next[j]] = [next[j], next[i]]
    onChange(next)
  }

  const add = () => {
    onChange([
      ...fields,
      {
        id: `field${fields.length + 1}`,
        label: `Field ${fields.length + 1}`,
        type: 'text',
      },
    ])
  }

  return (
    <Stack spacing={1}>
      {fields.map((field, i) => {
        const key = `${depth}-${i}`
        const isExpanded = expandedKey === key
        return (
          <Accordion
            key={key}
            expanded={isExpanded}
            onChange={(_, exp) => setExpandedKey(exp ? key : null)}
            disableGutters
            elevation={0}
            sx={{ border: 1, borderColor: 'divider' }}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ width: '100%', mr: 1 }}
                onClick={(e) => e.stopPropagation()}
              >
                <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
                  {field.label || field.id}
                </Typography>
                <Chip label={field.type} size="small" variant="outlined" />
                <IconButton size="small" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Up">
                  <KeyboardArrowUp fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  disabled={i === fields.length - 1}
                  onClick={() => move(i, 1)}
                  aria-label="Down"
                >
                  <KeyboardArrowDown fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => remove(i)} aria-label="Remove">
                  <Delete fontSize="small" />
                </IconButton>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <Stack direction="row" spacing={1}>
                  <TextField
                    label="Field ID (slug)"
                    value={field.id}
                    onChange={(e) =>
                      update(i, {
                        ...field,
                        id: e.target.value.replace(/[^a-zA-Z0-9-]/g, ''),
                      })
                    }
                    size="small"
                    sx={{ flex: 1 }}
                    helperText="lowercase, no spaces"
                  />
                  <TextField
                    label="Label"
                    value={field.label}
                    onChange={(e) => update(i, { ...field, label: e.target.value })}
                    size="small"
                    sx={{ flex: 2 }}
                  />
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Type</InputLabel>
                    <Select
                      label="Type"
                      value={field.type}
                      onChange={(e) => update(i, { ...field, type: e.target.value as NorthStarFieldType })}
                    >
                      {FIELD_TYPES.map((t) => (
                        <MenuItem key={t} value={t}>
                          {t}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>

                {field.type === 'enum' && (
                  <TextField
                    label="Options (comma-separated)"
                    value={(field.options || []).join(', ')}
                    onChange={(e) =>
                      update(i, {
                        ...field,
                        options: e.target.value
                          .split(',')
                          .map((s) => s.trim())
                          .filter(Boolean),
                      })
                    }
                    size="small"
                  />
                )}
                {field.type === 'dimensions' && (
                  <TextField
                    label="Axes (comma-separated)"
                    value={(field.axes || []).join(', ')}
                    onChange={(e) =>
                      update(i, {
                        ...field,
                        axes: e.target.value
                          .split(',')
                          .map((s) => s.trim())
                          .filter(Boolean),
                      })
                    }
                    size="small"
                    helperText="2–8 axes"
                  />
                )}
                {field.type === 'trajectory' && (
                  <TextField
                    label="Number of years"
                    type="number"
                    value={field.years ?? 10}
                    onChange={(e) =>
                      update(i, { ...field, years: Math.max(1, Math.min(30, Number(e.target.value) || 10)) })
                    }
                    size="small"
                    sx={{ width: 160 }}
                  />
                )}
                {field.type === 'repeater' && (
                  <Stack spacing={2}>
                    <Stack direction="row" spacing={1}>
                      <TextField
                        label="Min items"
                        type="number"
                        value={field.min ?? ''}
                        onChange={(e) =>
                          update(i, { ...field, min: e.target.value === '' ? undefined : Number(e.target.value) })
                        }
                        size="small"
                        sx={{ width: 120 }}
                      />
                      <TextField
                        label="Max items"
                        type="number"
                        value={field.max ?? ''}
                        onChange={(e) =>
                          update(i, { ...field, max: e.target.value === '' ? undefined : Number(e.target.value) })
                        }
                        size="small"
                        sx={{ width: 120 }}
                      />
                    </Stack>
                    {depth >= 1 ? (
                      <Typography variant="caption" color="error">
                        Repeater nesting is limited to 2 levels.
                      </Typography>
                    ) : (
                      <Box sx={{ pl: 2, borderLeft: 2, borderColor: 'primary.light' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                          Sub-fields (rendered per item)
                        </Typography>
                        <TemplateFieldsEditor
                          fields={field.fields || []}
                          onChange={(next) => update(i, { ...field, fields: next })}
                          depth={depth + 1}
                        />
                      </Box>
                    )}
                  </Stack>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>
        )
      })}
      <Box>
        <Button startIcon={<Add />} onClick={add} size="small">
          Add field
        </Button>
      </Box>
    </Stack>
  )
}
